import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, take, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { NumberModel } from '../../models/primitive/number.model';
import { AppNotificationType } from '../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../services/app-notification/app-notification.service';
import { BarcodeScannerService } from '../../services/barcode-scanner/barcode-scanner.service';
import { matchAndProcessBarcode } from '@evorait/pace-components';

@Component({
    selector: 'pace-core-number-input',
    templateUrl: './number-input.component.html',
    styleUrls: ['./number-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
})
export class NumberInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: NumberModel;

    @Output()
    public modelChange = new EventEmitter<NumberModel>();

    @Input()
    public ariaLabel = 'Number Input';

    @Input()
    public parentForm: FormArray | undefined;

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<NumberModel>>();

    public numberInputControl: FormControl;
    public faBarcode = faBarcode;

    private originalValue: number;
    private destroySubject = new Subject<void>();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly barcodeScanner: BarcodeScannerService,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnInit(): void {
        const numberValidator = (control: AbstractControl): ValidationErrors | null => this.checkValidity(control);
        this.numberInputControl = this.formBuilder.control(
            {
                value: this.model.value,
                disabled: this.model.options.allowReadonlyValidation ? false : this.model.options.readonly,
            },
            numberValidator,
        );

        this.numberInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: number) => {
                    const oldValue = this.model.value;
                    this.model.value = newVal;
                    this.modelChange.emit(this.model);
                    this.valueChange.emit({
                        oldValue: oldValue,
                        newValue: this.model.value,
                    });
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.numberInputControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in NumberInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model && this.numberInputControl) {
            if (this.model.options.readonly && this.numberInputControl.enabled) {
                this.numberInputControl.disable({ onlySelf: true, emitEvent: false });
            }
            if (!this.model.options.readonly && this.numberInputControl.disabled) {
                this.numberInputControl.enable({ onlySelf: true, emitEvent: false });
            }

            this.model = <NumberModel>changesObj.model.currentValue;
            this.numberInputControl.setValue(this.model.value);
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.numberInputControl);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public onFocus(): void {
        this.originalValue = typeof this.model.value === 'undefined' ? 0 : this.model.value;
    }

    public onBlur(): void {
        // Checking conditions to restore the original value:
        // 1. Is there a new value? If the entered value is invalid, angular will put 'undefined' into the bound model.
        //    This means for example entering a letter will result in 'undefined' as model value.
        // 2. Is the field cleared? ie value is now 'null', but it was a required field
        // 3. Is the new value larger than maxValue?
        // 4. Is the new value smaller than minValue?
        // If any of these conditions is true, we will restore the value that the model had before the user edited the
        // input field contents.
        if (
            typeof this.model.value === 'undefined' ||
            (this.model.value === null && this.model.options.required) ||
            this.model.value > this.model.options.maxValue ||
            this.model.value < this.model.options.minValue
        ) {
            this.model.value = this.originalValue;
            this.numberInputControl.setValue(this.model.value);
        }
    }

    public doScan(): void {
        this.barcodeScanner
            .scan(this.model.options.patternScan)
            .pipe(
                take(1),
                tap((scanResult) => {
                    // check if result matches validation pattern and patterns check should be in string always
                    this.log.debug('NumberInputComponent: doScan: scanned value:', JSON.stringify(scanResult));
                    const patternResult = matchAndProcessBarcode(scanResult, this.model.options.patternScan);
                    const parsedValue = ConfigConstants.validationParser.Decimal(patternResult);
                    this.numberInputControl.setValue(parsedValue);
                }),
                catchError((error: unknown) => {
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('NUMBER_TEXT_SCAN_ERROR_NO_TITLE'),
                        <string>(
                            this.translate.instant('NUMBER_TEXT_SCAN_ERROR_NO_BODY', { error: JSON.stringify(error) })
                        ),
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};

        const controlValue = <number>control.value;

        if (this.model.options.required && !controlValue && controlValue !== 0) {
            errors.required = { required: 'this input is mandatory' };
        }

        if (controlValue > this.model.options.maxValue) {
            errors.max = { max: 'max value exceeded' };
        }
        if (controlValue < this.model.options.minValue) {
            errors.min = { min: 'min value exceeded' };
        }
        if (controlValue && controlValue.toString().length > this.model.options.maxLength) {
            errors.maxlength = { maxlength: 'maxlength exceeded' };
        }
        if (controlValue && controlValue.toString().length < this.model.options.minLength) {
            errors.minlength = { minlength: 'minlength exceeded' };
        }
        const patternCheckResult =
            typeof control.value !== 'undefined' && control.value !== null
                ? controlValue.toString().match(this.model.options.pattern)
                : undefined;
        if (
            controlValue &&
            controlValue.toString().length > 0 &&
            typeof patternCheckResult !== 'undefined' &&
            (!patternCheckResult || parseFloat(patternCheckResult[0]) !== control.value)
        ) {
            errors.pattern = { pattern: 'pattern does not match', text: 'ERROR_ONLY_POSITIVE_INTEGER_NUMBERS_ALLOWED' };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
